<script setup lang="ts">
/**
 * FormFieldCheckboxes is a shorthand helper for creating one or more checkboxes in a form.
 * It wraps our usual Form components, but providing a shorter API to them.
 */

import { vAutoAnimate } from '@formkit/auto-animate/vue';
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage, } from '../index';
import { Checkbox } from '../../checkbox'

defineOptions({
  inheritAttrs: false,
});

const props = defineProps<{
  name: string;
  description?: string;
  id?: string;
  label?: string;
  required?: boolean;
  options?: Array<{ title: string; value: string; }>;
}>();

const fieldName = computed(() => props.id || props.name);
const requiredOnValue = computed(() => props.required && props.options?.length === 1);
</script>

<template>
  <FormField :name="fieldName" type="checkbox">
    <FormItem :id="fieldName" v-auto-animate>
      <FormLabel as="div">
        <slot />
        <template v-if="!$slots.default && props.label">
          {{ props.label }}<span v-if="props.required" class="label--required text-[color:var(--theme-highlight-color)]"> *</span>
        </template>
      </FormLabel>

      <FormField
        v-for="option in options"
        :key="option.value"
        :name="fieldName"
        type="checkbox"
        :unchecked-value="false"
        :value="option.value"
        v-slot="{ value, handleChange }"
      >
        <FormItem :id="option.value" class="flex flex-row items-center gap-8">
          <FormControl>
            <Checkbox
              :checked="value && value.includes(option.value)"
              @update:checked="handleChange"
            />
          </FormControl>
          <FormLabel class="font-normal">
            {{ option.title }}<span v-if="props.required && requiredOnValue" class="label--required text-[color:var(--theme-highlight-color)]"> *</span>
          </FormLabel>
        </FormItem>
      </FormField>

      <FormDescription v-if="props.description || $slots.description">
        <template v-if="!$slots.description">
          {{ props.description }}
        </template>
        <slot name="description" />
      </FormDescription>

      <FormMessage />
    </FormItem>
  </FormField>
</template>
